import classNames from "classnames";
import React, { useEffect } from "react";
import { isoWebPageURL } from "tsi-common-react/src/models/nominals";
import { t } from "ttag";

import { reverse } from "@reactivated";

import { getDocument, getWindow } from "@thelabnyc/thelabui/src/utils/dom";
import { richT } from "@thelabnyc/thelabui/src/utils/richText";

import gifts from "../../../../img/checkout/gifts.png";
import { ErrorBoundary } from "../../../components/ErrorBoundary";
import { ErrorModal } from "../../../components/ErrorModal";
import { Image } from "../../../components/Image";
import { Link } from "../../../components/Link";
import { IUserConfigurableBundle } from "../../../models/catalogue";
import { money } from "../../../utils/format";
import { range } from "../../../utils/functional";
import { Dinero } from "../../../utils/money";
import { usePageURL } from "../../../utils/urls";
import { ConfigureGift } from "../ConfigureGift";
import { ConfigureGiftStatus } from "../constants";

interface IProps {
    status: ConfigureGiftStatus;
    bundles: readonly IUserConfigurableBundle[];
    onAddToBasket: (event: React.MouseEvent<HTMLElement>) => void;
    totalValue: Dinero;

    // Add to basket error modal
    addToBasketErrorOpen: boolean;
    addToBasketErrorReason: string;
    onCloseErrorModal: () => void;
}

export const ConfigureGiftsWithPurchase = (props: IProps): React.ReactNode => {
    const homeURL = usePageURL("home");

    useEffect(() => {
        // Prevent browser from restoring scroll positions when using the back button
        const window = getWindow();
        const history = window?.history;
        if (history && "scrollRestoration" in history) {
            history.scrollRestoration = "manual";
        }
    }, []);

    const onCloseErrorModal = (
        e: React.FormEvent<Element> | React.KeyboardEvent<Element>,
    ) => {
        e.preventDefault();
        props.onCloseErrorModal();
    };

    const userConfigurableBundles = () => {
        return props.bundles.length > 0;
    };

    const buildTotalValue = () => {
        if (!userConfigurableBundles()) {
            return;
        }

        const formattedValue = money(props.totalValue);
        // TODO(i18n): Article "a" should be included in translation string for proper localization (some languages don't use articles or use different structures)
        return <p>{t`a ${formattedValue} value`}</p>;
    };

    const buildGifts = () => {
        let giftNumericalOrder = 0;
        return props.bundles.map((bundle, giftIdx) => {
            return range(bundle.quantity).map((qtyIdx) => {
                const giftID = `${bundle.id}-${giftIdx}-${qtyIdx}`;
                giftNumericalOrder++;
                return (
                    <ErrorBoundary key={giftID}>
                        <ConfigureGift
                            bundle={bundle}
                            giftID={giftID}
                            giftIdx={giftNumericalOrder}
                            qtyIdx={qtyIdx}
                        />
                    </ErrorBoundary>
                );
            });
        });
    };

    const buildErrorModal = () => {
        const reason = props.addToBasketErrorReason;
        return (
            <ErrorModal
                name="configurator"
                isOpen={props.addToBasketErrorOpen}
                onRequestClose={onCloseErrorModal}
            >
                <p>{t`Product could not be added to cart (${reason}).`}</p>
                <p>{t`Please adjust your selection and try again.`}</p>
            </ErrorModal>
        );
    };

    const buildCTA = () => {
        const isAdding = props.status === ConfigureGiftStatus.ADDING_TO_BASKET;
        const isComplete = props.status === ConfigureGiftStatus.COMPLETE;
        const isError = props.status === ConfigureGiftStatus.SELECTION_ERROR;
        const errorMsg = (
            <p>
                {t`Sorry, the gift(s) you selected are currently unavailable. Please adjust your selections.`}
            </p>
        );
        let buttonText: string = t`Add gifts to cart`;
        if (isAdding) {
            buttonText = t`Adding…`;
        } else if (isComplete) {
            buttonText = t`Done!`;
        }
        if (isError) {
            const document = getDocument();
            if (document) {
                const configureGiftsWithPurchase =
                    document.querySelector<HTMLElement>(
                        ".configure-gifts-with-purchase",
                    );
                const unavailableMessage =
                    configureGiftsWithPurchase &&
                    configureGiftsWithPurchase.querySelectorAll<HTMLElement>(
                        ".configurator__unavailable",
                    );
                const firstUnavailableMessage =
                    unavailableMessage && unavailableMessage[0];
                Array.from(
                    document.querySelectorAll<HTMLElement>(
                        ".configure-gift__content",
                    ),
                ).forEach((elem) => {
                    if (elem.contains(firstUnavailableMessage)) {
                        elem.scrollIntoView({ behavior: "smooth" });
                    }
                });
            }
        }

        return (
            <section className="configure-gifts-with-purchase__add">
                {isError ? errorMsg : null}
                <button
                    className="button button--primary button--full-width"
                    onClick={props.onAddToBasket}
                    disabled={isAdding || isComplete}
                >
                    {buttonText}
                </button>
                {buildTotalValue()}
            </section>
        );
    };

    const buildCtaWithNoProducts = () => {
        return (
            <section className="configure-gifts-with-purchase__add configure-gifts-with-purchase__add--no-configuration">
                <Link
                    href={isoWebPageURL.wrap(reverse("basket:summary"))}
                    target="_top"
                    className="button button--primary button--full-width"
                >
                    {t`Return to Cart`}
                </Link>
                <Link
                    href={homeURL}
                    target="_top"
                    className="button button--primary button--full-width button--inverse"
                >
                    {t`Continue Shopping`}
                </Link>
            </section>
        );
    };

    const heading = userConfigurableBundles()
        ? richT(
              t`Your cart qualifies for a <strong>free</strong> gift.<br />Please customize your item(s) below:`,
          )
        : richT(
              t`Congratulations!<br />You have successfully selected your <strong>free</strong> gift`,
          );
    const containerClassNames = classNames({
        "l-capped-width": true,
        "configure-gifts-with-purchase": true,
        "configure-gifts-with-purchase--no-configuration":
            !userConfigurableBundles(),
    });
    const giftIcon = gifts;
    return (
        <div className={containerClassNames}>
            <header>
                <Image src={giftIcon} alt="" />
                <p className="pre-header">{t`GIFT BUNDLE`}</p>
                <h1>{heading}</h1>
                {buildTotalValue()}
            </header>
            {buildGifts()}
            {userConfigurableBundles() ? buildCTA() : buildCtaWithNoProducts()}
            {buildErrorModal()}
        </div>
    );
};
